'use client';

import { cn } from '@/lib/utils';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { motion } from 'framer-motion';
import { GROQImageLoader } from '@/sanity/lib/definitions';
import { PageTextOnlyLPOldDesignSchema } from '@/sanity/pages/PageTextOnlyLPOldDesignTemplate';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from '@/shadcn/ui/carousel';
import NextImage from 'next/image';
import Image from '@/helpers/Image';
import iqicon from '@/public/affiliate/iqIcon.svg';
import { useState, useEffect, useRef } from 'react';
import Autoplay from 'embla-carousel-autoplay';

export default function TextWithCertificateSection({
  data,
  className,
}: {
  data: PageTextOnlyLPOldDesignSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);

  const autoplayRef = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false }),
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
      setProgress(0);
    });
  }, [api]);

  useEffect(() => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          return 0;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [current]);
  return (
    <section
      className={cn(
        'relative overflow-clip pb-[3rem] lg:pb-[9rem] px-[2rem] lg:px-[7rem] w-full',
        className,
      )}
    >
      <div className="w-full flex flex-col items-start relative bg-white">
        {data.textWithCertificateList &&
          data.textWithCertificateList.length > 0 && (
            <div className="w-full relative">
              <Carousel
                opts={{
                  // align: "start",
                  loop: true,
                }}
                plugins={[autoplayRef.current]}
                setApi={setApi}
                className="w-full "
              >
                <CarouselContent className="w-full">
                  {data.textWithCertificateList.map((item, index) => (
                    <CarouselItem key={index} className="basis-full px-[1rem]">
                      {item.image?.src && (
                        <div
                          className="relative w-full rounded-[2rem] overflow-hidden"
                          style={{
                            aspectRatio:
                              item.image.width && item.image.height
                                ? `${item.image.width} / ${item.image.height}`
                                : '8192 / 5642',
                          }}
                        >
                          <NextImage
                            src={item.image.src}
                            alt={item.image.name ?? `Certificate ${index + 1}`}
                            fill
                            sizes="(max-width: 1024px) 100vw, 90vw"
                            placeholder={item.image.lqip ? 'blur' : 'empty'}
                            blurDataURL={item.image.lqip}
                            className="object-contain"
                            priority={index === 0}
                          />
                        </div>
                      )}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {/* Segmented Progress Bar */}
              {count > 0 && (
                <div className="flex gap-[0.5rem] mt-[2rem] justify-center w-[95%] mx-auto">
                  {Array.from({ length: count }).map((_, index) => (
                    <div
                      key={index}
                      className="h-[0.375rem] rounded-full overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        backgroundColor: '#0000001A',
                        flexGrow: current === index ? 2 : 1,
                      }}
                    >
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          backgroundColor: '#6DA991',
                          width: current === index ? `${progress}%` : '0%',
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mt-[5.75rem] px-[3rem] lg:px-0"
        >
          {/*<ChipSerif text={data.exclusiveChip} />*/}
          <Image
            src={iqicon}
            alt="iqicon"
            className="!w-[6.25rem] lg:!w-[8.625rem] mb-[2.5rem]"
          />
        </motion.div>

        {data.textWithCertificateTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext richtext-start prose-h2:font-medium prose-h2:text-[3rem] lg:prose-h2:text-[4.25rem] lg:prose-h2:tracking-[-0.1175rem] prose-h2:leading-[124%] lg:prose-h2:mb-[0rem]
            lg:prose-p:text-[2.25rem] prose-p:text-[#103037]/80 px-[3rem] lg:px-0"
          >
            <PortableText
              value={data.textWithCertificateTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
